// ⚠️ PERFORM CORE — Ne pas modifier sans validation complète
// Connecté à : programs
// Utilisé dans : Dashboard, ProgramViewer, PlanningWithProposals
import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useCyclePlanning } from "@/hooks/useCyclePlanning";
import type { WeekType } from "@/types/weekTypes";

export interface CurrentProgramInfo {
  id: string;
  title: string;
  activeWeekTypes: string[] | null;
}

interface ProgramQueryResult {
  legacyProgramId: string | null;
  weekTypePrograms: CurrentProgramInfo[];
}

async function fetchProgramData(userId: string): Promise<ProgramQueryResult> {
  // Fetch legacy current_program_id
  const { data: profile } = await supabase
    .from("user_intent_profiles")
    .select("current_program_id")
    .eq("user_id", userId)
    .maybeSingle();

  const candidateId = (profile as any)?.current_program_id || null;

  let legacyProgramId: string | null = null;

  // P2 fix: passive guard — verify the referenced program still exists
  if (candidateId) {
    const { data: exists } = await supabase
      .from("programs")
      .select("id")
      .eq("id", candidateId)
      .maybeSingle();
    legacyProgramId = exists ? candidateId : null;
  }

  // Fetch all programs that have active_week_types set
  const { data: activeProgs } = await supabase
    .from("programs")
    .select("id, title, active_week_types")
    .eq("user_id", userId)
    .not("active_week_types", "is", null);

  const weekTypePrograms: CurrentProgramInfo[] = (activeProgs as any[] || [])
    .filter((p) => p.active_week_types && p.active_week_types.length > 0)
    .map((p) => ({
      id: p.id,
      title: p.title,
      activeWeekTypes: p.active_week_types,
    }));

  return { legacyProgramId, weekTypePrograms };
}

/**
 * Resolves the active program based on the current week type (cycle mode)
 * or falls back to the legacy current_program_id (non-cycle mode).
 *
 * Backed by React Query — staleTime 60s, deduplicated across all consumers.
 * Invalidate via queryClient.invalidateQueries({ queryKey: ['current-program'] }).
 */
export function useCurrentProgram() {
  const { user } = useAuth();
  const { cycle, currentWeekStatus } = useCyclePlanning();
  const queryClient = useQueryClient();

  const isCycleMode = cycle?.enabled ?? false;
  const currentWeekType = currentWeekStatus?.type as WeekType | undefined;

  const { data, isLoading: loading } = useQuery({
    queryKey: ["current-program", user?.id],
    queryFn: () => fetchProgramData(user!.id),
    enabled: !!user,
    staleTime: 60_000, // 60s — coordinated with program lifecycle
    gcTime: 5 * 60_000,
  });

  const legacyProgramId = data?.legacyProgramId ?? null;
  const weekTypePrograms = data?.weekTypePrograms ?? [];

  // Resolve the current program based on mode
  const currentProgram = useMemo((): CurrentProgramInfo | null => {
    if (isCycleMode && currentWeekType) {
      const match = weekTypePrograms.find(
        (p) => p.activeWeekTypes?.includes(currentWeekType)
      );
      if (match) return match;
    }

    // Fallback: legacy single program
    if (legacyProgramId) {
      const fromWeekType = weekTypePrograms.find((p) => p.id === legacyProgramId);
      if (fromWeekType) return fromWeekType;
      return { id: legacyProgramId, title: "", activeWeekTypes: null };
    }

    return null;
  }, [isCycleMode, currentWeekType, weekTypePrograms, legacyProgramId]);

  // Get program for a specific week type
  const getProgramForWeekType = useCallback((weekType: WeekType): CurrentProgramInfo | null => {
    return weekTypePrograms.find((p) => p.activeWeekTypes?.includes(weekType)) || null;
  }, [weekTypePrograms]);

  const refetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["current-program"] });
  }, [queryClient]);

  return {
    currentProgramId: currentProgram?.id || null,
    currentProgram: currentProgram ? { id: currentProgram.id, title: currentProgram.title } : null,
    weekTypePrograms,
    getProgramForWeekType,
    isCycleMode,
    currentWeekType,
    loading,
    refetch,
  };
}
